import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Layout from '../components/Layout';
import { programService } from '../services/firebaseService';
import { Program } from '../types';

const ProgramDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProgram = async () => {
      if (!id) {
        setError('Program ID is required');
        setLoading(false);
        return;
      }

      try {
        const programData = await programService.getProgramById(id);
        if (programData) {
          setProgram(programData);
        } else {
          setError('Program not found');
        }
      } catch (err) {
        console.error('Error loading program:', err);
        setError('Failed to load program');
      } finally {
        setLoading(false);
      }
    };

    loadProgram();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="loading"><div className="spinner" /></div>
        </div>
      </Layout>
    );
  }

  if (error || !program) {
    return (
      <Layout>
        <div className="container">
          <div className="error">{error || 'Program not found'}</div>
          <Link to="/programs" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Back to Programs
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <Link 
          to="/programs" 
          className="btn btn-secondary"
          style={{ marginBottom: '2rem', display: 'inline-block' }}
        >
          ← Back to Programs
        </Link>
        
        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {program.imageUrl && (
            <img 
              src={program.imageUrl} 
              alt={program.title} 
              style={{ 
                width: '100%', 
                borderRadius: '8px', 
                marginBottom: '2rem' 
              }} 
            />
          )}
          <h1>{program.title}</h1>
          {program.summary && (
            <div style={{ fontSize: '1.25rem', fontWeight: 500, marginBottom: '1.5rem', color: 'var(--text-dark)' }}>
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p style={{ marginBottom: '0' }}>{children}</p>,
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                      {children}
                    </a>
                  )
                }}
              >
                {program.summary}
              </ReactMarkdown>
            </div>
          )}
          {program.description && (
            <div 
              style={{ 
                marginTop: '2rem',
                lineHeight: '1.8',
                color: 'var(--text-dark)'
              }}
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p style={{ marginBottom: '1rem' }}>{children}</p>,
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                      {children}
                    </a>
                  )
                }}
              >
                {program.description}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProgramDetailPage;

